import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, projectIdParamsSchema, sseQuerySchema } from '../schemas';
import { notificationSchema } from '../../interfaces/INotification';
import { distressButtonNotificationMetadataSchema } from '../../interfaces/IDistressButtonNotificationMetadata';
import { managerSchema } from '../../interfaces/IManager';

// ============================================================
// SSE stream route (standalone — registered with implement())
// ============================================================

/**
 * NOT MOVED, deliberately, and the path below is spelled in full for that
 * reason. `notificationsContract` is `{ root: true }`, which zeroes the prefix
 * `notifications.routes.ts` passes to `implement()` — so a path of `/stream`
 * would silently relocate this route to `/api/notification/stream`. It
 * compiles, every typed client is unaffected (there is none), and both
 * subscribers build the URL by hand as a string. That is the failure that hit
 * Activities twice, both times on an SSE stream.
 *
 * An SSE URL is also a pub/sub CHANNEL KEY: `sse.plugin.ts::getClients` matches
 * on the delimiter-aware PARTIAL rule (`matchesPartialStreamApi`), so the
 * publisher's key must be a delimiter-terminated substring of the subscriber's
 * sanitized `request.url`. `?projectId=` is FIRST in both subscribers' URLs,
 * which is what lets the publisher narrow the fan-out to one project.
 * `sseStreamUrls.test.ts` pins the path, the publisher key, and the
 * relationship between them.
 *
 * `notificationUsername` also stays: a member of a project can still stream
 * another member's feed by changing it. Narrowing this stream to the owning
 * user is a separate decision, flagged and deliberately not taken here.
 */
export const notificationsStreamRoute = route({
	method: 'GET',
	path: '/notifications/stream',
	query: sseQuerySchema.extend({
		projectId: requiredStringSchema,
		notificationUsername: requiredStringSchema,
		daysAgo: z.coerce.number().optional(),
	}),
	response: z.any().describe('SSE stream of user notifications'),
	operationId: 'getNotificationsStream',
	tags: ['Notifications'],
	summary: 'Stream notifications for a user (SSE)',
});

// ============================================================
// Contract (non-SSE routes)
// ============================================================

const notificationParamsSchema = projectIdParamsSchema.extend({
	notificationId: requiredStringSchema.describe('Notification identifier'),
});

/**
 * The merged tag an SOS ingest carries has its own top-level `projectId` — the
 * project the tag is currently IN, read downstream by the notification
 * pipeline. It is deliberately NOT declared: declaring it alongside the path
 * param is the "two places at once" shape the audit rejects and CLAUDE.md
 * forbids. `looseObject` passes it through, and the handler asserts it equals
 * the path param with a loud 400 naming both values. Same resolution as
 * SafetyEngine's twin ingest routes, so one event has one URI convention.
 */
const sosAlertBodySchema = z.looseObject({
	tagId: requiredStringSchema,
	name: z.string().optional(),
	manager: managerSchema.optional(),
	closestAnchor: z.looseObject({
		floorNick: requiredStringSchema,
		areaNick: requiredStringSchema,
	}),
});

export const notificationsContract = defineContract(
	'notifications',
	{
		/**
		 * SELF scope. This was `PUT /notifications/:username/markAllAsViewed`, and
		 * the handler used `request.params.username` rather than the token — so any
		 * member of a project could mark another member's notifications read. The
		 * identity leaves the URI entirely rather than being validated.
		 *
		 * `/viewed` is an explicit sub-resource rather than a flag on the
		 * collection: the playbook warns against squeezing a bulk verb into a
		 * collection verb.
		 */
		markAllAsViewed: route({
			method: 'PUT',
			path: '/projects/:projectId/notifications/viewed',
			params: projectIdParamsSchema,
			response: z.string(),
			operationId: 'markAllUserNotificationsAsViewed',
			tags: ['Notifications'],
			summary: "Mark all of the caller's notifications as viewed in a project",
		}),
		updateNotification: route({
			method: 'PUT',
			path: '/projects/:projectId/notifications/:notificationId',
			params: notificationParamsSchema,
			body: z
				.object({
					isViewed: z.boolean().optional(),
					isVisible: z.boolean().optional(),
				})
				.refine((data) => data.isViewed !== undefined || data.isVisible !== undefined, {
					message: 'At least one of isViewed or isVisible must be provided',
				}),
			response: notificationSchema,
			operationId: 'updateNotification',
			tags: ['Notifications'],
			summary: 'Update a notification (viewed/visible status)',
		}),
		sosAlert: route({
			method: 'POST',
			path: '/projects/:projectId/notifications/sosAlert',
			params: projectIdParamsSchema,
			body: sosAlertBodySchema,
			response: distressButtonNotificationMetadataSchema,
			operationId: 'createSosAlert',
			tags: ['Notifications'],
			summary: 'Trigger a distress button (SOS) alert notification',
		}),
	},
	{ root: true }
);
